import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { useApiRequest } from '../../api/useApiRequest';
import { IfUserHasPermission } from '../../authorisation/IfUserHasPermission';
import { useInterval } from '../../helpers/hooks/useInterval';
import { useOnMount } from '../../helpers/hooks/useOnMount';
import { useTemporaryBoolean } from '../../helpers/hooks/useTemporaryBoolean';
import { RouteComponentProps } from '../../helpers/RouteComponentProps';
import { ReactComponent as RefreshIcon } from '../../images/icons/refresh.svg';
import { PageContainer } from '../../layout/Layout';
import { Alert } from '../../shared/Alert';
import { ErrorBadge, NeutralBadge, SuccessBadge } from '../../shared/Badge';
import { ButtonIconLeft, SecondaryProgressButton } from '../../shared/Button';
import { ErrorBox } from '../../shared/ErrorBox';
import { Header1 } from '../../shared/Headers';
import { PageLoadingIndicator } from '../../shared/LoadingIndicator';
import { Table, TBody, Td, Th, THead, Tr } from '../../shared/Table';
import { fadeIn, flash } from '../../styling/animations';
import { spacing06 } from '../../styling/spacing';

type NetSuiteSynchronisationInformationComponent = {
  (props: RouteComponentProps): JSX.Element | null;
};

export type NetSuiteSynchronisationInformationResponse = {
  customersLastSynchronisedDateTime: string | null;
  synchroniseCustomersJobStatus: string | null;
  projectsLastSynchronisedDateTime: string | null;
  synchroniseProjectsJobStatus: string | null;
  opportunitiesLastSynchronisedDateTime: string | null;
  synchroniseOpportunitiesJobStatus: string | null;
  partsLastSynchronisedDateTime: string | null;
  synchronisePartsJobStatus: string | null;
};

export const forceUpdatePartsDataTestId = 'force-update-parts-data-button';
export const forceUpdateHistoricalPartsDataTestId = 'force-update-historical-parts-data-button';
export const forceUpdateHistoricalCustomersDataTestId = 'force-update-historical-customers-data-button';

export const NetSuiteSynchronisationInformation: NetSuiteSynchronisationInformationComponent = () => {
  const fetchNetSuiteSynchronisationInformationRequest = useApiRequest<NetSuiteSynchronisationInformationResponse>({
    httpMethod: 'get',
    endpointUrl: 'netsuite/synchronisation-information',
  });

  const manuallyTriggerAllHistoricalCustomersUpdateRequest = useApiRequest({
    httpMethod: 'post',
    endpointUrl: 'customers/manually-trigger-all-customers-in-database-update',
  });

  const manuallyTriggerPartsUpdateRequest = useApiRequest({
    httpMethod: 'post',
    endpointUrl: 'parts/manually-trigger-parts-update',
  });

  const manuallyTriggerAllHistoricalPartsUpdateRequest = useApiRequest({
    httpMethod: 'post',
    endpointUrl: 'parts/manually-trigger-all-historical-parts-update',
  });

  useOnMount(() => {
    void fetchNetSuiteSynchronisationInformationRequest.sendRequest();
  });

  const [latestResponse, setLatestResponse] = useState<NetSuiteSynchronisationInformationResponse | null>();

  useEffect(() => {
    if (fetchNetSuiteSynchronisationInformationRequest.response != null) {
      setLatestResponse(fetchNetSuiteSynchronisationInformationRequest.response);
    }
  }, [fetchNetSuiteSynchronisationInformationRequest.response]);

  const initialResponse = useRef<NetSuiteSynchronisationInformationResponse | null>(null);

  useEffect(() => {
    if (initialResponse.current == null && latestResponse != null) {
      initialResponse.current = latestResponse;
    }
  }, [initialResponse, latestResponse]);

  useInterval(() => {
    void fetchNetSuiteSynchronisationInformationRequest.sendRequest();
  }, 10 * 1000);

  const [shouldShowUpdateQueuedAlert, showUpdateQueuedAlert] = useTemporaryBoolean(6 * 1000);

  if (fetchNetSuiteSynchronisationInformationRequest.error != null) {
    return (
      <PageContainer>
        <ErrorBox error={fetchNetSuiteSynchronisationInformationRequest.error} />
      </PageContainer>
    );
  }

  if (latestResponse == null && fetchNetSuiteSynchronisationInformationRequest.inProgress) {
    return <PageLoadingIndicator />;
  }

  if (latestResponse != null) {
    return (
      <PageContainer>
        {manuallyTriggerAllHistoricalCustomersUpdateRequest.error != null && (
          <ApiErrorBox error={manuallyTriggerAllHistoricalCustomersUpdateRequest.error} />
        )}
        {manuallyTriggerPartsUpdateRequest.error != null && (
          <ApiErrorBox error={manuallyTriggerPartsUpdateRequest.error} />
        )}
        {manuallyTriggerAllHistoricalPartsUpdateRequest.error != null && (
          <ApiErrorBox error={manuallyTriggerAllHistoricalPartsUpdateRequest.error} />
        )}
        {shouldShowUpdateQueuedAlert && (
          <UpdateQueuedAlert
            type="success"
            title="Parts Update Queued"
            body="Parts information will be synchronised from NetSuite - please allow a few moments for the update to complete."
          />
        )}
        <Header1>NetSuite Sync Info</Header1>
        <SyncInfoTable>
          <THead>
            <Tr>
              <SynchronisationTableHeader>Sync</SynchronisationTableHeader>
              <LastPerformedTableHeader>Last Completed</LastPerformedTableHeader>
              <StatusTableHeader>Status</StatusTableHeader>
            </Tr>
          </THead>
          <TBody>
            <Tr>
              <Td>Customers</Td>
              <Td>
                <LastSyncedTime
                  $shouldFlash={
                    initialResponse.current != null &&
                    latestResponse.customersLastSynchronisedDateTime !==
                      initialResponse.current.customersLastSynchronisedDateTime
                  }
                  key={latestResponse.customersLastSynchronisedDateTime || undefined}
                >
                  {latestResponse.customersLastSynchronisedDateTime || <>&mdash;</>}
                </LastSyncedTime>
              </Td>
              <Td>
                <JobStatusBadge status={latestResponse.synchroniseCustomersJobStatus} />
              </Td>
              <Td />
              <Td>
                <IfUserHasPermission requiredPermission="Manually Trigger Customers Update">
                  <SecondaryProgressButton
                    onClick={() =>
                      manuallyTriggerAllHistoricalCustomersUpdateRequest.sendRequest({
                        onSuccess: showUpdateQueuedAlert,
                      })
                    }
                    inProgress={manuallyTriggerAllHistoricalCustomersUpdateRequest.inProgress}
                    disabled={shouldShowUpdateQueuedAlert}
                    data-testid={forceUpdateHistoricalCustomersDataTestId}
                  >
                    <ButtonIconLeft>
                      <RefreshIcon />
                    </ButtonIconLeft>
                    Force Update Historical Data
                  </SecondaryProgressButton>
                </IfUserHasPermission>
              </Td>
            </Tr>
            <Tr>
              <Td>Projects</Td>
              <Td>
                <LastSyncedTime
                  $shouldFlash={
                    initialResponse.current != null &&
                    latestResponse.projectsLastSynchronisedDateTime !==
                      initialResponse.current.projectsLastSynchronisedDateTime
                  }
                  key={latestResponse.projectsLastSynchronisedDateTime || undefined}
                >
                  {latestResponse.projectsLastSynchronisedDateTime || <>&mdash;</>}
                </LastSyncedTime>
              </Td>
              <Td>
                <JobStatusBadge status={latestResponse.synchroniseProjectsJobStatus} />
              </Td>
              <Td />
              <Td />
            </Tr>
            <Tr>
              <Td>Opportunities</Td>
              <Td>
                <LastSyncedTime
                  $shouldFlash={
                    initialResponse.current != null &&
                    latestResponse.opportunitiesLastSynchronisedDateTime !==
                      initialResponse.current.opportunitiesLastSynchronisedDateTime
                  }
                  key={latestResponse.opportunitiesLastSynchronisedDateTime || undefined}
                >
                  {latestResponse.opportunitiesLastSynchronisedDateTime || <>&mdash;</>}
                </LastSyncedTime>
              </Td>
              <Td>
                <JobStatusBadge status={latestResponse.synchroniseOpportunitiesJobStatus} />
              </Td>
              <Td />
              <Td />
            </Tr>
            <Tr>
              <Td>Parts</Td>
              <Td>
                <LastSyncedTime
                  $shouldFlash={
                    initialResponse.current != null &&
                    latestResponse.partsLastSynchronisedDateTime !==
                      initialResponse.current.partsLastSynchronisedDateTime
                  }
                  key={latestResponse.partsLastSynchronisedDateTime || undefined}
                >
                  {latestResponse.partsLastSynchronisedDateTime || <>&mdash;</>}
                </LastSyncedTime>
              </Td>
              <Td>
                <JobStatusBadge status={latestResponse.synchronisePartsJobStatus} />
              </Td>
              <Td>
                <IfUserHasPermission requiredPermission="Manually Trigger Parts Update">
                  <SecondaryProgressButton
                    onClick={() =>
                      manuallyTriggerPartsUpdateRequest.sendRequest({
                        onSuccess: showUpdateQueuedAlert,
                      })
                    }
                    inProgress={manuallyTriggerPartsUpdateRequest.inProgress}
                    disabled={shouldShowUpdateQueuedAlert}
                    data-testid={forceUpdatePartsDataTestId}
                  >
                    <ButtonIconLeft>
                      <RefreshIcon />
                    </ButtonIconLeft>
                    Force Update
                  </SecondaryProgressButton>
                </IfUserHasPermission>
              </Td>
              <Td>
                <IfUserHasPermission requiredPermission="Manually Trigger Parts Update">
                  <SecondaryProgressButton
                    onClick={() =>
                      manuallyTriggerAllHistoricalPartsUpdateRequest.sendRequest({
                        onSuccess: showUpdateQueuedAlert,
                      })
                    }
                    inProgress={manuallyTriggerAllHistoricalPartsUpdateRequest.inProgress}
                    disabled={shouldShowUpdateQueuedAlert}
                    data-testid={forceUpdateHistoricalPartsDataTestId}
                  >
                    <ButtonIconLeft>
                      <RefreshIcon />
                    </ButtonIconLeft>
                    Force Update Historical Data
                  </SecondaryProgressButton>
                </IfUserHasPermission>
              </Td>
            </Tr>
          </TBody>
        </SyncInfoTable>
      </PageContainer>
    );
  }

  return null;
};

const JobStatusBadge = ({ status }: { status: string | null }) => {
  if (status === 'Succeeded') {
    return <SuccessBadge>{status}</SuccessBadge>;
  }

  if (status === 'Failed') {
    return <ErrorBadge>{status}</ErrorBadge>;
  }

  return <NeutralBadge>{status}</NeutralBadge>;
};

const ApiErrorBox = styled(ErrorBox)`
  margin-bottom: ${spacing06};
`;

const SyncInfoTable = styled(Table)`
  width: auto;
  table-layout: fixed;
`;

const SynchronisationTableHeader = styled(Th)`
  width: 200px;
`;

const LastPerformedTableHeader = styled(Th)`
  width: 180px;
`;

const StatusTableHeader = styled(Th)`
  width: 180px;
`;

const LastSyncedTime = styled.div<{ $shouldFlash: boolean }>`
  ${props =>
    props.$shouldFlash &&
    css`
      animation: ${flash} 3s ease both;
    `};
  letter-spacing: 1px;
`;

const UpdateQueuedAlert = styled(Alert)`
  animation: ${fadeIn} 0.5s ease;
  margin-bottom: ${spacing06};
`;
